// MysqlInstancesView.jsx
// Admin tab for managing MySQL connection registrations. List + add + edit +
// delete + test-connection. Same dark theme as the other admin views.

import { useEffect, useState } from 'react';
import {
  Database, Plus, RefreshCw, Edit2, Trash2, X, CheckCircle2, AlertTriangle
} from 'lucide-react';
import mysqlInstanceService from '../services/mysqlInstanceService.js';

const C = {
  pageBg: '#0b0f19', cardBg: '#0f172a', border: '#1e293b', input: '#020617',
  text: '#e5e7eb', muted: '#9ca3af', accent: '#a78bfa',
  good: '#22c55e', warn: '#f59e0b', bad: '#ef4444'
};
const ipt = {
  padding: '6px 10px', background: C.input, border: `1px solid ${C.border}`,
  borderRadius: 6, color: C.text, fontSize: 12, outline: 'none', width: '100%'
};
const btn = { ...ipt, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto' };

const EMPTY = {
  instanceId: '', label: '', host: '', port: 3306,
  user: '', password: '', defaultDatabase: '',
  isReadReplica: false, ownerTenantId: '', allowedTenants: '',
  maxPoolPerDb: '', enabled: true
};

export default function MysqlInstancesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);   // an instance or {...EMPTY} when adding

  async function loadAll() {
    setLoading(true);
    try {
      const list = await mysqlInstanceService.list();
      setItems(list || []);
      setError(null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadAll(); }, []);

  async function handleDelete(inst) {
    if (!confirm(`Delete MySQL connection "${inst.label}" (${inst.instanceId})?`)) return;
    try { await mysqlInstanceService.remove(inst.instanceId); loadAll(); }
    catch (err) { setError(err.message); }
  }

  async function handleTest(inst) {
    try {
      const res = await mysqlInstanceService.test(inst.instanceId, inst.defaultDatabase || undefined);
      if (res.ok) alert(`✓ Connection OK to "${inst.label}"`);
      else        alert(`✗ Connection failed: ${res.code || ''} ${res.message || 'unknown error'}`);
    } catch (err) { alert(`✗ Connection failed: ${err.message}`); }
  }

  return (
    <div style={{ padding: 20, background: C.pageBg, color: C.text, minHeight: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 22 }}>
            <Database size={20} /> MySQL connections
          </h2>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
            {loading ? 'Loading…' : `${items.length} connection${items.length === 1 ? '' : 's'} registered`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn} onClick={loadAll}><RefreshCw size={14} /></button>
          <button style={{ ...btn, background: C.accent, color: '#fff', borderColor: C.accent }}
                  onClick={() => setEditing({ ...EMPTY })}>
            <Plus size={14} /> Add connection
          </button>
        </div>
      </div>

      {error && <div style={errBox}>{error}</div>}

      {items.length === 0 && !loading ? (
        <div style={{ ...emptyState, marginTop: 24 }}>
          No MySQL connections registered yet. Your env-based MYSQL_HOST connection is still active as "default".
          Click "Add connection" to register additional MySQL servers (cloud RDS, read replica, per-tenant DB, etc).
        </div>
      ) : (
        <table style={tbl}>
          <thead>
            <tr>
              <th style={th}>Label</th>
              <th style={th}>ID</th>
              <th style={th}>Host:Port</th>
              <th style={th}>User</th>
              <th style={th}>Default DB</th>
              <th style={th}>Tenants</th>
              <th style={th}>Replica</th>
              <th style={th}>Enabled</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(inst => (
              <tr key={inst.instanceId} style={{ opacity: inst.enabled ? 1 : 0.5 }}>
                <td style={td}><b>{inst.label}</b></td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{inst.instanceId}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{inst.host}:{inst.port}</td>
                <td style={td}>{inst.user}</td>
                <td style={td}>{inst.defaultDatabase || '—'}</td>
                <td style={td}>
                  {inst.ownerTenantId
                    ? <span title="Owner">{inst.ownerTenantId}</span>
                    : (inst.allowedTenants?.length
                        ? <span title="Allowed">{inst.allowedTenants.join(', ')}</span>
                        : <span style={{ color: C.muted }}>global</span>)}
                </td>
                <td style={td}>{inst.isReadReplica ? 'yes' : 'no'}</td>
                <td style={td}>
                  {inst.enabled
                    ? <span style={badge('good')}>ON</span>
                    : <span style={badge('bad')}>OFF</span>}
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button style={btn} title="Test connection" onClick={() => handleTest(inst)}>
                      <CheckCircle2 size={12} />
                    </button>
                    <button style={btn} title="Edit" onClick={() => setEditing(inst)}>
                      <Edit2 size={12} />
                    </button>
                    <button style={{ ...btn, color: C.bad }} title="Delete" onClick={() => handleDelete(inst)}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <EditorModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAll(); }}
        />
      )}
    </div>
  );
}

function EditorModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial._id || !!initial.instanceId && !!initial.host;   // existing record has populated host
  // Normalize for the form (e.g. allowedTenants array → comma string)
  const [form, setForm] = useState(() => ({
    ...initial,
    allowedTenants: Array.isArray(initial.allowedTenants) ? initial.allowedTenants.join(', ') : (initial.allowedTenants || ''),
    password: ''   // never pre-fill — server doesn't return it; user types if they want to change
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function set(field, v) { setForm(f => ({ ...f, [field]: v })); }

  async function save() {
    setSaving(true); setError(null);
    try {
      const body = {
        instanceId:      form.instanceId.trim(),
        label:           form.label.trim(),
        host:            form.host.trim(),
        port:            parseInt(form.port, 10) || 3306,
        user:            form.user.trim(),
        defaultDatabase: form.defaultDatabase.trim(),
        isReadReplica:   !!form.isReadReplica,
        ownerTenantId:   form.ownerTenantId.trim() || null,
        allowedTenants:  String(form.allowedTenants).split(',').map(s => s.trim()).filter(Boolean),
        maxPoolPerDb:    form.maxPoolPerDb ? parseInt(form.maxPoolPerDb, 10) : null,
        enabled:         !!form.enabled
      };
      if (form.password) body.password = form.password;  // only send if user typed it
      if (isEdit) await mysqlInstanceService.update(initial.instanceId, body);
      else        await mysqlInstanceService.create(body);
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <span style={{ fontWeight: 600 }}>{isEdit ? `Edit "${initial.label}"` : 'New MySQL connection'}</span>
          <button style={btn} onClick={onClose}><X size={14} /></button>
        </div>
        <div style={modalBody}>
          {error && <div style={errBox}>{error}</div>}

          <Section title="Identity">
            <Row label="ID (slug)">
              <input style={ipt} value={form.instanceId} disabled={isEdit}
                     placeholder="e.g. primary-rds, tenantA-prod"
                     onChange={(e) => set('instanceId', e.target.value)} />
            </Row>
            <Row label="Label">
              <input style={ipt} value={form.label}
                     placeholder="Friendly name shown in UI dropdowns"
                     onChange={(e) => set('label', e.target.value)} />
            </Row>
          </Section>

          <Section title="Connection">
            <Row label="Host">
              <input style={ipt} value={form.host} placeholder="rds.example.com or 10.0.0.4"
                     onChange={(e) => set('host', e.target.value)} />
            </Row>
            <Row label="Port">
              <input type="number" style={ipt} value={form.port}
                     onChange={(e) => set('port', e.target.value)} />
            </Row>
            <Row label="User">
              <input style={ipt} value={form.user} placeholder="db username"
                     onChange={(e) => set('user', e.target.value)} />
            </Row>
            <Row label="Password">
              <input type="password" style={ipt} value={form.password}
                     placeholder={isEdit ? 'leave blank to keep existing' : 'set a password'}
                     onChange={(e) => set('password', e.target.value)} />
            </Row>
            <Row label="Default database">
              <input style={ipt} value={form.defaultDatabase}
                     placeholder="optional — e.g. scada"
                     onChange={(e) => set('defaultDatabase', e.target.value)} />
            </Row>
          </Section>

          <Section title="Access & policy">
            <Row label="Owner tenant">
              <input style={ipt} value={form.ownerTenantId}
                     placeholder="plantId, or leave blank for global"
                     onChange={(e) => set('ownerTenantId', e.target.value)} />
            </Row>
            <Row label="Allowed tenants">
              <input style={ipt} value={form.allowedTenants}
                     placeholder="comma-separated plantIds (in addition to owner)"
                     onChange={(e) => set('allowedTenants', e.target.value)} />
            </Row>
            <Row label="Read replica">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={!!form.isReadReplica}
                       onChange={(e) => set('isReadReplica', e.target.checked)} />
                This connection is a read replica (reports will prefer it)
              </label>
            </Row>
            <Row label="Pool size override">
              <input type="number" style={ipt} value={form.maxPoolPerDb}
                     placeholder="leave blank to use MYSQL_POOL_PER_DB_MAX env default"
                     onChange={(e) => set('maxPoolPerDb', e.target.value)} />
            </Row>
            <Row label="Enabled">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={!!form.enabled}
                       onChange={(e) => set('enabled', e.target.checked)} />
                Active (uncheck to temporarily disable without deleting)
              </label>
            </Row>
          </Section>
        </div>
        <div style={modalFooter}>
          <button style={btn} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...btn, background: C.accent, color: '#fff', borderColor: C.accent }}
                  onClick={save} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── shared style helpers ─────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 10, alignItems: 'center' }}>
      <label style={{ color: C.muted, fontSize: 12 }}>{label}</label>
      {children}
    </div>
  );
}
function badge(kind) {
  const colors = {
    good: { bg: 'rgba(34,197,94,0.12)',  color: '#86efac' },
    bad:  { bg: 'rgba(239,68,68,0.12)',  color: '#fca5a5' },
    warn: { bg: 'rgba(245,158,11,0.12)', color: '#fdba74' }
  }[kind] || {};
  return { padding: '2px 8px', borderRadius: 4, fontSize: 11, background: colors.bg, color: colors.color };
}

const errBox     = { padding: 8, background: 'rgba(239,68,68,0.1)', color: '#fca5a5', borderRadius: 6, fontSize: 12, border: '1px solid rgba(239,68,68,0.3)', marginBottom: 12 };
const emptyState = { padding: 24, textAlign: 'center', color: C.muted, fontSize: 13, background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 8 };
const tbl        = { width: '100%', borderCollapse: 'collapse', fontSize: 12, color: C.text, background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' };
const th         = { textAlign: 'left', padding: '8px 12px', borderBottom: `1px solid ${C.border}`, color: C.muted, fontWeight: 600, background: '#0c1322' };
const td         = { padding: '8px 12px', borderBottom: `1px solid ${C.border}` };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalBox     = { width: 'min(720px, 90vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text };
const modalHeader  = { padding: 14, borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const modalBody    = { padding: 14, overflowY: 'auto', flex: 1 };
const modalFooter  = { padding: 14, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 };
